import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../api';
import { Plus, Edit2, Trash2, UserCog, X, Shield } from 'lucide-react';

const ROLES = [
  { value: 'admin', label: 'Administrador', cls: 'bg-purple-100 text-purple-700' },
  { value: 'operador', label: 'Operador', cls: 'bg-blue-100 text-blue-700' },
  { value: 'financeiro', label: 'Financeiro', cls: 'bg-emerald-100 text-emerald-700' },
];

const getRoleCls = (r) => ROLES.find(o => o.value === r)?.cls || 'bg-slate-100 text-slate-700';
const getRoleLabel = (r) => ROLES.find(o => o.value === r)?.label || r;

const emptyForm = { nome: '', email: '', senha: '', role: 'operador', ativo: true };

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/usuarios');
      setUsuarios(data);
    } catch { toast.error('Erro ao carregar usuários'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setShowPwd(false); setModalOpen(true); };
  const openEdit = (u) => { setEditingId(u.id); setForm({ nome: u.nome, email: u.email, senha: '', role: u.role, ativo: u.ativo }); setShowPwd(false); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingId && !payload.senha) delete payload.senha;
      if (editingId) {
        await api.put(`/usuarios/${editingId}`, payload);
        toast.success('Usuário atualizado!');
      } else {
        await api.post('/usuarios', payload);
        toast.success('Usuário criado com sucesso!');
      }
      closeModal();
      fetchUsuarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar usuário');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/usuarios/${deleteId}`);
      toast.success('Usuário excluído!');
      setDeleteId(null);
      fetchUsuarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="space-y-5 page-enter" data-testid="usuarios-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Usuários do Sistema</h1>
          <p className="text-slate-500 text-sm mt-0.5">{usuarios.length} usuário(s) cadastrado(s)</p>
        </div>
        <button onClick={openCreate} data-testid="btn-novo-usuario"
          className="flex items-center gap-2 bg-[#d72d21] hover:bg-[#b91c1c] text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
          <Plus className="w-4 h-4" /> Novo Usuário
        </button>
      </div>

      {/* Permission info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Perfis de Acesso:</p>
            <p className="text-amber-700 mt-1">
              <strong>Admin:</strong> Acesso completo ao sistema. &nbsp;|&nbsp;
              <strong>Operador:</strong> Clientes, Viagens, Reservas e Encomendas. &nbsp;|&nbsp;
              <strong>Financeiro:</strong> Visão operacional e dados financeiros.
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : usuarios.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <UserCog className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhum usuário encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Usuário</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Perfil</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usuarios.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`usuario-row-${u.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm flex-shrink-0">
                          {u.nome.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{u.nome}</p>
                          {u.id === currentUser?.id && <span className="text-xs text-orange-500">(você)</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-500 hidden md:table-cell text-xs">{u.email}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleCls(u.role)}`}>
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(u)} data-testid={`btn-edit-usuario-${u.id}`}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {u.id !== currentUser?.id && (
                          <button onClick={() => setDeleteId(u.id)} data-testid={`btn-delete-usuario-${u.id}`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="usuario-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingId ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nome completo *</label>
                <input value={form.nome} onChange={(e) => f('nome', e.target.value)} required
                  data-testid="usuario-nome-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => f('email', e.target.value)} required
                  data-testid="usuario-email-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Senha {editingId ? '(deixe em branco para manter)' : '*'}
                </label>
                <div className="relative">
                  <input type={showPwd ? 'text' : 'password'} value={form.senha} onChange={(e) => f('senha', e.target.value)}
                    required={!editingId} minLength={6} data-testid="usuario-senha-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 focus:border-[#0147a0] pr-10" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs hover:text-slate-600">
                    {showPwd ? 'Ocultar' : 'Exibir'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Perfil de acesso</label>
                <select value={form.role} onChange={(e) => f('role', e.target.value)} data-testid="usuario-role-select"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0147a0]/20 bg-white">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {editingId && (
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="ativo" checked={form.ativo} onChange={(e) => f('ativo', e.target.checked)}
                    className="w-4 h-4 accent-orange-500" data-testid="usuario-ativo-checkbox" />
                  <label htmlFor="ativo" className="text-sm font-medium text-slate-700">Usuário ativo</label>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} data-testid="btn-salvar-usuario"
                  className="flex-1 bg-[#d72d21] hover:bg-[#b91c1c] text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-60">
                  {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Criar Usuário')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Deseja excluir este usuário permanentemente?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium">Cancelar</button>
              <button onClick={handleDelete} data-testid="btn-confirm-delete-usuario"
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
